import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DeliveryDiningRounded from '@mui/icons-material/DeliveryDiningRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import RestaurantRounded from '@mui/icons-material/RestaurantRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { money, relativeTime } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  REJECT_REASONS,
  useOrderActions,
  type FoodpandaOrder,
  type OrderStatus,
  type RejectReason,
} from './api';
import { FoodpandaWhatsapp } from './FoodpandaWhatsapp';
import { RefundDialog } from './RefundDialog';

const STATUS: Record<
  OrderStatus,
  { label: string; color: 'info' | 'warning' | 'success' | 'error' | 'default' }
> = {
  RECEIVED: { label: 'New', color: 'info' },
  ACCEPTED: { label: 'Preparing', color: 'warning' },
  READY: { label: 'Ready', color: 'success' },
  PICKED_UP: { label: 'Picked up', color: 'default' },
  REJECTED: { label: 'Rejected', color: 'error' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
  FAILED: { label: 'Needs attention', color: 'error' },
};

export function OrderStatusChip({ status }: { status: OrderStatus }) {
  const s = STATUS[status];
  return <Chip size="small" label={s.label} color={s.color} variant="outlined" />;
}

const PREP_CHOICES = [10, 15, 20, 25, 30, 45, 60];

/**
 * One Foodpanda order with the next step for it: accept (with the preparation time) or reject,
 * ready, picked up. Picked-up orders that could not be invoiced can be completed again.
 */
export function OrderCard({
  order,
  canOrder,
  defaultPrep,
  simulator,
  onPrint,
}: {
  order: FoodpandaOrder;
  canOrder: boolean;
  defaultPrep: number;
  /** The shop is in simulator mode (lets a manager play Foodpanda cancelling the order). */
  simulator: boolean;
  onPrint: (order: FoodpandaOrder) => void;
}) {
  const { format } = useShop();
  const notify = useNotify();
  const actions = useOrderActions();
  const [prep, setPrep] = useState(order.prepMinutes ?? defaultPrep);
  const [rejecting, setRejecting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const { can } = useAuth();
  const busy = Object.values(actions).some((m) => m.isPending);
  const v = { id: order.id, version: order.version };
  const run = (p: Promise<FoodpandaOrder>, done: string) =>
    p.then((o) => (o.error ? notify.info(o.error) : notify.success(done)), notify.error);

  const delivery = order.expeditionType !== 'pickup';
  return (
    <Card variant="outlined" data-testid={`fp-order-${order.code}`}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            #{order.code}
          </Typography>
          <OrderStatusChip status={order.status} />
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', color: 'text.secondary', mb: 1, flexWrap: 'wrap' }}
        >
          {delivery ? (
            <DeliveryDiningRounded fontSize="small" aria-hidden />
          ) : (
            <ShoppingBagRounded fontSize="small" aria-hidden />
          )}
          <Typography variant="body2">
            {delivery ? 'Delivery' : 'Pick-up'} · {relativeTime(order.placedAt ?? order.createdAt)}
            {order.customerName ? ` · ${order.customerName}` : ''}
            {order.customerPhone ? ` · ${order.customerPhone}` : ''}
          </Typography>
        </Stack>

        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {order.items.map((item, i) => (
            <Box component="li" key={i} sx={{ py: 0.25 }}>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 28 }}>
                  {Number(item.quantity)}×
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {item.name}
                </Typography>
              </Stack>
              {item.toppings.map((t, j) => (
                <Typography key={j} variant="caption" color="text.secondary" sx={{ pl: 4.5 }}>
                  + {Number(t.quantity) > 1 ? `${t.quantity}× ` : ''}
                  {t.name}
                  <br />
                </Typography>
              ))}
              {item.comment && (
                <Typography variant="caption" color="warning.main" sx={{ pl: 4.5 }}>
                  “{item.comment}”
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        {order.customerNote && (
          <Alert severity="info" icon={false} sx={{ mt: 1, py: 0 }}>
            Note: {order.customerNote}
          </Alert>
        )}
        <Divider sx={{ my: 1.25 }} />
        <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {Number(order.shopDiscount) > 0
              ? `Your voucher ${money(order.shopDiscount, format)}`
              : order.paymentType
                ? `Paid: ${order.paymentType}`
                : ''}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {money(order.total, format)}
          </Typography>
        </Stack>
        {(order.salesOrder ?? order.invoice) && (
          <Typography variant="caption" color="text.secondary">
            {order.salesOrder && (
              <Link component={RouterLink} to={`/sales/orders/${order.salesOrder.id}`}>
                {order.salesOrder.number}
              </Link>
            )}
            {order.invoice && (
              <>
                {' · '}
                <Link component={RouterLink} to={`/sales/invoices/${order.invoice.id}`}>
                  {order.invoice.number}
                </Link>
              </>
            )}
          </Typography>
        )}
        {order.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {order.error}
          </Alert>
        )}
        {order.needsRefund && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Foodpanda cancelled this order after pick-up
            {order.cancelReason ? ` (${order.cancelReason})` : ''}. Record the refund.
          </Alert>
        )}
        {order.refunds.map((r) => (
          <Typography
            key={r.salesReturn.id}
            variant="caption"
            color="text.secondary"
            component="div"
          >
            Refunded: {r.salesReturn.number}
            {r.creditNote
              ? ` · credit note ${r.creditNote.number} ${money(r.creditNote.total, format)}`
              : ''}
          </Typography>
        ))}
        {order.status === 'REJECTED' && order.rejectReason && (
          <Typography variant="caption" color="text.secondary">
            Rejected:{' '}
            {REJECT_REASONS.find((r) => r.value === order.rejectReason)?.label ??
              order.rejectReason}
          </Typography>
        )}
        {order.status === 'CANCELLED' && order.cancelReason && (
          <Typography variant="caption" color="text.secondary">
            Cancelled by Foodpanda: {order.cancelReason}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          {canOrder && order.status === 'RECEIVED' && (
            <>
              <TextField
                select
                size="small"
                label="Ready in"
                value={prep}
                onChange={(e) => setPrep(Number(e.target.value))}
                sx={{ width: 110 }}
              >
                {[...new Set([...PREP_CHOICES, defaultPrep])]
                  .sort((a, b) => a - b)
                  .map((m) => (
                    <MenuItem key={m} value={m}>
                      {m} min
                    </MenuItem>
                  ))}
              </TextField>
              <Button
                variant="contained"
                startIcon={<CheckRounded />}
                disabled={busy}
                onClick={() =>
                  run(actions.accept.mutateAsync({ ...v, prepMinutes: prep }), 'Order accepted')
                }
              >
                Accept
              </Button>
            </>
          )}
          {canOrder && (order.status === 'RECEIVED' || order.status === 'FAILED') && (
            <Button
              color="error"
              startIcon={<CloseRounded />}
              disabled={busy}
              onClick={() => setRejecting(true)}
            >
              Reject
            </Button>
          )}
          {canOrder && order.status === 'ACCEPTED' && (
            <Button
              variant="contained"
              startIcon={<RestaurantRounded />}
              disabled={busy}
              onClick={() => run(actions.ready.mutateAsync(v), 'Marked ready')}
            >
              Ready
            </Button>
          )}
          {canOrder && (order.status === 'ACCEPTED' || order.status === 'READY') && (
            <Button
              variant={order.status === 'READY' ? 'contained' : 'outlined'}
              startIcon={<DeliveryDiningRounded />}
              disabled={busy}
              onClick={() => run(actions.pickedUp.mutateAsync(v), 'Picked up and invoiced')}
            >
              Picked up
            </Button>
          )}
          {canOrder && order.status === 'PICKED_UP' && order.error && (
            <Button
              variant="contained"
              disabled={busy}
              onClick={() => run(actions.complete.mutateAsync(v), 'Delivered and invoiced')}
            >
              Deliver and invoice
            </Button>
          )}
          {canOrder && can('sales.return') && order.status === 'PICKED_UP' && order.invoice && (
            <Button
              color="warning"
              variant={order.needsRefund ? 'contained' : 'text'}
              startIcon={<UndoRounded />}
              disabled={busy}
              onClick={() => setRefunding(true)}
            >
              Foodpanda refund
            </Button>
          )}
          {order.status === 'PICKED_UP' && <FoodpandaWhatsapp order={order} />}
          {['RECEIVED', 'ACCEPTED', 'READY'].includes(order.status) && (
            <Button startIcon={<PrintRounded />} onClick={() => onPrint(order)}>
              Kitchen ticket
            </Button>
          )}
          {simulator && ['RECEIVED', 'ACCEPTED', 'READY', 'PICKED_UP'].includes(order.status) && (
            <Button
              size="small"
              color="inherit"
              disabled={busy || order.needsRefund || order.refunds.length > 0}
              onClick={() =>
                run(actions.simulateCancel.mutateAsync(v), 'Foodpanda cancelled the order')
              }
            >
              Test: Foodpanda cancels
            </Button>
          )}
        </Stack>
      </CardContent>
      {refunding && <RefundDialog order={order} onClose={() => setRefunding(false)} />}
      {rejecting && (
        <RejectDialog
          onClose={() => setRejecting(false)}
          busy={actions.reject.isPending}
          onReject={(reason, message) =>
            void run(actions.reject.mutateAsync({ ...v, reason, message }), 'Order rejected').then(
              () => setRejecting(false),
            )
          }
        />
      )}
    </Card>
  );
}

function RejectDialog({
  onClose,
  onReject,
  busy,
}: {
  onClose: () => void;
  onReject: (reason: RejectReason, message?: string) => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState<RejectReason>('TOO_BUSY');
  const [message, setMessage] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Reject the order</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as RejectReason)}
          >
            {REJECT_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Message to Foodpanda (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Back</Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy}
          onClick={() => onReject(reason, message.trim() || undefined)}
        >
          Reject order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
